import { useState } from 'react';
import { SignalState } from '../../types';
import './Dashboard.css';

// Updated colors to match design system tokens
const SIGNAL_COLORS: Record<string, string> = {
  seeking: 'var(--signal-seeking)',
  fear: 'var(--signal-fear)',
  care: 'var(--signal-care)',
  panic: 'var(--signal-panic)',
  play: 'var(--signal-play)',
  rage: 'var(--signal-rage)',
  energy: 'var(--signal-energy)',
  satisfaction: 'var(--signal-satisfaction)',
  shame: 'var(--signal-shame)',
  boredom: 'var(--signal-boredom)',
  confusion: 'var(--signal-confusion)',
  nostalgia: 'var(--signal-nostalgia)',
  existentialAnxiety: 'var(--signal-anxiety)',
  arousal: 'var(--signal-drive)',
};

// Human-friendly labels
const SIGNAL_LABELS: Record<string, string> = {
  seeking: 'Seeking',
  fear: 'Fear',
  care: 'Care',
  panic: 'Panic',
  play: 'Play',
  rage: 'Rage',
  energy: 'Energy',
  satisfaction: 'Satisfaction',
  shame: 'Shame',
  boredom: 'Boredom',
  confusion: 'Confusion',
  nostalgia: 'Nostalgia',
  existentialAnxiety: 'Anxiety',
  arousal: 'Drive',
};

// All known signal keys
const ALL_SIGNALS: (keyof SignalState)[] = [
  'seeking', 'care', 'play', 'energy', 'satisfaction',
  'fear', 'panic', 'rage',
  'shame', 'boredom', 'confusion', 'nostalgia', 'existentialAnxiety',
  'arousal',
];

interface SignalBarsProps {
  signals: SignalState;
  baselines: SignalState;
}

// Tier thresholds per spec
const DOMINANT_THRESHOLD = 0.5;
const NOTABLE_THRESHOLD = 0.1;

type Tier = 'dominant' | 'notable' | 'quiet';

function getTier(value: number): Tier {
  if (value >= DOMINANT_THRESHOLD) return 'dominant';
  if (value >= NOTABLE_THRESHOLD) return 'notable';
  return 'quiet';
}

export function SignalBars({ signals, baselines }: SignalBarsProps) {
  const [quietExpanded, setQuietExpanded] = useState(false);

  // Sort all signals by value descending, then group by tier
  const sorted = ALL_SIGNALS
    .filter(key => typeof signals[key] === 'number')
    .map(key => ({
      key,
      value: signals[key],
      baseline: baselines[key] ?? 0,
      tier: getTier(signals[key]),
    }))
    .sort((a, b) => b.value - a.value);

  const dominant = sorted.filter(s => s.tier === 'dominant');
  const notable = sorted.filter(s => s.tier === 'notable');
  const quiet = sorted.filter(s => s.tier === 'quiet');

  return (
    <div className="panel signal-bars-panel">
      <h3 className="panel-title">Emotional Signals</h3>

      {/* Dominant tier (≥0.5) — always shown, prominent */}
      {dominant.length > 0 && (
        <div className="signal-tier signal-tier--dominant">
          <div className="signal-tier-label">Dominant</div>
          {dominant.map(s => (
            <SignalRow key={s.key} signalKey={s.key} value={s.value} baseline={s.baseline} tier="dominant" />
          ))}
        </div>
      )}

      {/* Notable tier (0.1–0.5) — always shown */}
      {notable.length > 0 && (
        <div className="signal-tier signal-tier--notable">
          <div className="signal-tier-label">Notable</div>
          {notable.map(s => (
            <SignalRow key={s.key} signalKey={s.key} value={s.value} baseline={s.baseline} tier="notable" />
          ))}
        </div>
      )}

      {/* Quiet tier (<0.1) — collapsed by default */}
      {quiet.length > 0 && (
        <div className="signal-tier signal-tier--quiet">
          <button
            className="signal-tier-toggle"
            onClick={() => setQuietExpanded(!quietExpanded)}
            aria-expanded={quietExpanded}
          >
            <span className="signal-tier-label">Quiet</span>
            <span className="signal-tier-count">{quiet.length}</span>
            <span className={`signal-tier-chevron ${quietExpanded ? 'expanded' : ''}`}>▸</span>
          </button>
          {quietExpanded && quiet.map(s => (
            <SignalRow key={s.key} signalKey={s.key} value={s.value} baseline={s.baseline} tier="quiet" />
          ))}
        </div>
      )}
    </div>
  );
}

// Individual signal row with tier-aware rendering
function SignalRow({ signalKey, value, baseline, tier }: {
  signalKey: string;
  value: number;
  baseline: number;
  tier: Tier;
}) {
  const color = SIGNAL_COLORS[signalKey] || 'var(--signal-seeking)';
  const label = SIGNAL_LABELS[signalKey] || signalKey;
  const deviation = Math.abs(value - baseline);
  const isNoteworthy = deviation >= 0.15;

  // Tier-differentiated bar height
  const barHeight = tier === 'dominant' ? 8 : tier === 'notable' ? 6 : 4;
  // Tier-differentiated opacity
  const barOpacity = tier === 'quiet' ? 0.5 : 1;

  return (
    <div className={`signal-item signal-item--${tier} ${isNoteworthy ? 'signal-noteworthy' : ''}`}>
      <span className="signal-label">{label}</span>
      <div className="signal-bar-container" style={{ height: barHeight }}>
        <div
          className="signal-bar-fill"
          style={{
            width: `${value * 100}%`,
            backgroundColor: color,
            opacity: barOpacity,
            ...(tier === 'dominant' && value >= 0.7 ? { boxShadow: `0 0 6px ${color}` } : {}),
          }}
        />
        <div
          className="signal-baseline-marker"
          style={{ left: `${baseline * 100}%` }}
          title={`Baseline: ${baseline.toFixed(2)}`}
        />
      </div>
      <span className="signal-value">{value.toFixed(2)}</span>
    </div>
  );
}
